/*
  Carnevore Mountain: Bird Pickup & Delivery's date picker inside the cart drawer.
  Loaded by snippets/cart-drawer.liquid when Theme settings > Cart > "Show the Bird Pickup & Delivery date
  picker" is on.

  Why this exists: Bird's app embed only loads its date picker (window.BirdApp) on the /cart page, and hides
  checkout buttons everywhere else. The drawer opens on every page, so:

  - Off the cart page (Bird not loaded): the drawer's Checkout button reads "Choose delivery date" and sends the
    shopper to /cart?cm_date=1. The empty date box is hidden. Nobody can check out without a date.
  - On /cart?cm_date=1: the drawer opens by itself and scrolls to Bird's date picker.
  - Once Bird is loaded (the cart page): gates checkout with Bird's storefront API (`await BirdApp.ready`, then
    sdk.selections.canCheckout(), refreshed through sdk.subscribe()). While it can't go ahead,
    #CartDrawer-BirdGate shows why and pressing checkout scrolls to the widget instead of submitting.
  - Keeps the widget: the drawer repaints .drawer__inner after every cart change, which would throw Bird's
    widget away. A MutationObserver swaps each repaint's empty #birdchime-slots-box for the live one.
  - On the cart page only, fails open: if Bird doesn't load within READY_TIMEOUT, checkout works as normal.
*/
(() => {
  const BOX_ID = 'birdchime-slots-box';
  const READY_TIMEOUT = 10000;
  const DATE_PARAM = 'cm_date';

  const drawer = document.querySelector('cart-drawer');
  if (!drawer || drawer.dataset.cmBird) return;
  drawer.dataset.cmBird = 'true';

  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const onCartPage = window.location.pathname.replace(/\/+$/, '') === cartUrl.replace(/\/+$/, '');

  // The box Bird drew its widget into. The first one on the page, until a repaint brings a new one.
  let liveBox = document.getElementById(BOX_ID);
  let sdk = null;
  // Bird's last answer. `true` until Bird says otherwise.
  let canProceed = true;
  // Checkout was pressed without a date: the message shows as an error until a date is chosen.
  let attempted = false;
  let refreshQueued = false;

  // No Bird on this page: checkout goes to the cart page to pick a date first.
  const sendToCart = () => !sdk && !onCartPage;

  // ---------- keeping the widget across repaints ----------

  const keepWidget = () => {
    const current = drawer.querySelector(`#${BOX_ID}`);
    if (!current) return;
    if (!liveBox || !liveBox.hasChildNodes()) {
      liveBox = current;
      return;
    }
    if (current !== liveBox) current.replaceWith(liveBox);
  };

  new MutationObserver(() => {
    keepWidget();
    queueRefresh();
  }).observe(drawer, { childList: true, subtree: true });

  // ---------- asking Bird whether checkout can go ahead ----------

  const readVerdict = (result) => {
    if (typeof result === 'boolean') return result;
    const value = result && (result.canProceed ?? result.canCheckout ?? result.ok);
    return typeof value === 'boolean' ? value : true;
  };

  const refresh = async () => {
    refreshQueued = false;
    if (sdk && sdk.selections && typeof sdk.selections.canCheckout === 'function') {
      try {
        canProceed = readVerdict(await sdk.selections.canCheckout());
      } catch (error) {
        console.warn('Bird canCheckout failed; checkout stays open.', error);
        canProceed = true;
      }
    }
    if (canProceed) attempted = false;
    paint();
  };

  function queueRefresh() {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(refresh);
  }

  // The footer repaints after every cart change, so the button and message are painted again each time.
  function paint() {
    const redirect = sendToCart();
    drawer.classList.toggle('cm-bird-off', redirect);

    const button = drawer.querySelector('#CartDrawer-Checkout');
    const label = button && button.querySelector('.cm-checkout__label');
    if (label) {
      if (!label.dataset.cmOriginal) label.dataset.cmOriginal = label.textContent.trim();
      const wanted = redirect ? button.dataset.cmBirdLabel || 'Choose delivery date' : label.dataset.cmOriginal;
      if (label.textContent.trim() !== wanted) label.textContent = wanted;
    }

    const gate = drawer.querySelector('#CartDrawer-BirdGate');
    if (!gate) return;
    const empty = drawer.classList.contains('is-empty');
    let message = '';
    if (!empty && redirect) {
      message = gate.dataset.redirectMessage || 'You’ll choose your delivery or pickup date on the next page.';
    }
    else if (!empty && !canProceed) message = gate.dataset.message || '';
    if (gate.textContent !== message) gate.textContent = message;
    gate.hidden = !message;
    gate.classList.toggle('is-attempted', !redirect && !canProceed && attempted);
  }

  const waitForBird = () =>
    new Promise((resolve) => {
      const started = Date.now();
      const check = () => {
        if (window.BirdApp && window.BirdApp.ready) {
          Promise.resolve(window.BirdApp.ready).then(resolve, () => resolve(null));
          return;
        }
        if (Date.now() - started > READY_TIMEOUT) return resolve(null);
        setTimeout(check, 200);
      };
      check();
    });

  paint();

  waitForBird().then((ready) => {
    sdk = ready && ready.selections ? ready : window.BirdApp && window.BirdApp.selections ? window.BirdApp : null;
    if (!sdk) {
      if (onCartPage) {
        console.warn('Bird Pickup & Delivery did not load on the cart page; drawer checkout is not gated.');
      }
      paint();
      return;
    }
    if (typeof sdk.subscribe === 'function') sdk.subscribe(queueRefresh);
    queueRefresh();
  });

  // ---------- opening the date picker after a redirect ----------

  const showPicker = () => {
    const section = drawer.querySelector('[data-cm-bird]');
    if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const box = drawer.querySelector(`#${BOX_ID}`);
    const first = box && box.querySelector('input, select, button, [tabindex]:not([tabindex="-1"])');
    if (first) setTimeout(() => first.focus({ preventScroll: true }), 300);
  };

  if (onCartPage) {
    const url = new URL(window.location.href);
    if (url.searchParams.has(DATE_PARAM)) {
      url.searchParams.delete(DATE_PARAM);
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      if (!drawer.classList.contains('is-empty')) {
        customElements.whenDefined('cart-drawer').then(() => {
          if (typeof drawer.open === 'function') drawer.open();
          setTimeout(showPicker, 500);
        });
      }
    }
  }

  // ---------- checkout ----------

  // Capture phase on the drawer runs before assets/cart-drawer.js locks checkout on the same submit.
  drawer.addEventListener(
    'submit',
    (event) => {
      if (event.target.id !== 'CartDrawer-Form') return;

      if (sendToCart()) {
        event.preventDefault();
        event.stopPropagation();
        window.location.href = `${cartUrl}?${DATE_PARAM}=1`;
        return;
      }

      if (canProceed) return;
      event.preventDefault();
      event.stopPropagation();
      attempted = true;
      paint();
      showPicker();
      queueRefresh();
    },
    true,
  );
})();
